'use client'

import { useCallback, useEffect, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import type { SelectPrice } from '@package/drizzle/schema'
import { Select, SelectContent, SelectGroup, SelectPrimitive } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

type PriceData = RouterOutputs['price']['getActivePrices']

/** The "Populer" chip, identical on the trigger and on every option. */
const FAVOURITE_CHIP_CLASS = cn(
  typographyVariants({ variant: 'label-sm' }),
  'srd:bg-purple-100 srd:text-neutral-white srd:rounded-sm srd:px-1 srd:py-0.5',
)

/** The "Hemat x%" note beside a term that costs less per month than the shortest one. */
const SAVINGS_NOTE_CLASS = cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-purple-100')

export interface SelectPriceProps {
  getAllPrice: PriceData
  /** Uncontrolled initial selection. Ignored when `value` is provided. */
  defaultPriceId?: string
  /**
   * Controlled selection. When set, internal state is bypassed entirely and the parent owns
   * the value — required to bind this to a TanStack Form field.
   */
  value?: string
  containerClassName?: string
  className?: string
  onValueChange?: (price: PriceData[number]) => void
  disabled?: boolean
  /**
   * Whether options carry the "Populer" chip and the "Hemat x%" note.
   *
   * False where the term is not a choice — an add-on inherits the term of the pool it joins, so a
   * badge advertising a cheaper option beside a disabled select is an offer the page cannot honour.
   */
  showBadges?: boolean
}

export function SelectPrice({
  getAllPrice,
  defaultPriceId: incomingDefaultPriceId,
  value,
  containerClassName,
  className,
  onValueChange,
  disabled,
  showBadges = true,
}: SelectPriceProps) {
  const isControlled = value !== undefined

  const defaultPriceId = (() => {
    if (incomingDefaultPriceId) return incomingDefaultPriceId

    const lastPrice = getAllPrice[getAllPrice.length - 1]

    if (lastPrice) return lastPrice.id

    const firstPrice = getAllPrice[0]

    if (firstPrice) return firstPrice.id

    throw new Error('No price found')
  })()

  const [selectedPriceId, setSelectedPriceId] = useState(defaultPriceId)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [selectedPrice, setSelectedPrice] = useState(getAllPrice.find((v) => v.id === defaultPriceId)!)

  // Controlled mode bypasses internal state entirely: the render reads only this derived
  // value, so the two sources of truth can never drift apart.
  const effectivePriceId = isControlled ? value : selectedPriceId

  /**
   * The row to render, which may not be the row the id names.
   *
   * Under volume pricing `getAllPrice` is NARROWED to the buyer's tier and re-narrows as the seat
   * count moves, so a held id can stop being in the list — the 1-9 row the buyer picked is simply
   * absent once they reach 10 seats. Both lookups here used to be `!`-asserted, so that produced
   * `undefined.duration` and took the whole checkout down at the tier boundary.
   *
   * Falling back to the first row keeps the control rendering. It is a DISPLAY fallback only: the
   * parent owns which row is charged, and re-points it to the same term in the new tier.
   */
  const heldPrice = isControlled ? getAllPrice.find((v) => v.id === value) : selectedPrice
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const effectivePrice = heldPrice ?? getAllPrice[0]!

  const theMostExpensivePrice = getAllPrice.reduce((cheapest, current) =>
    current.price > cheapest.price ? current : cheapest,
  )

  const handleChange = useCallback(
    (nextId: string) => {
      if (!isControlled) setSelectedPriceId(nextId)
      const price = getAllPrice.find((v) => v.id === nextId)

      if (price) onValueChange?.(price)
    },
    [getAllPrice, isControlled, onValueChange],
  )

  useEffect(() => {
    if (isControlled) return

    // Only when the held id is still in the list. Writing the `undefined` a narrowed list returns
    // is what made the render crash a tick later.
    const next = getAllPrice.find((v) => v.id === selectedPriceId)
    if (next) setSelectedPrice(next)
  }, [selectedPriceId, getAllPrice, isControlled])

  return (
    <Select
      value={effectivePriceId}
      onValueChange={(nextId) => {
        if (nextId) handleChange(nextId)
      }}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        className={cn(
          typographyVariants({ variant: 'body-sm' }),
          'srd:flex srd:h-10 srd:w-full srd:items-center srd:justify-between srd:rounded-md srd:border srd:border-input srd:bg-neutral-white srd:px-3 srd:py-2 srd:focus:outline-none srd:focus:ring-2 srd:focus:ring-offset-2 srd:disabled:cursor-not-allowed srd:disabled:opacity-50 srd:[&>span]:line-clamp-1',
          'srd:border-blue-100 srd:ring-offset-blue-50 srd:focus:ring-blue-30 srd:data-placeholder:text-blue-50 srd:text-neutral-black',
          className,
        )}
        disabled={disabled}
      >
        <SelectPrimitive.Value className={typographyVariants({ variant: 'body-md' })}>
          {effectivePrice.duration} Bulan
        </SelectPrimitive.Value>

        <div className='srd:flex srd:items-center srd:gap-x-2'>
          {showBadges && effectivePrice.isFavorite && <span className={FAVOURITE_CHIP_CLASS}>Populer</span>}

          {showBadges && getAllPrice.findIndex((v) => v.id === effectivePriceId) !== 0 && (
            <span className={SAVINGS_NOTE_CLASS}>
              Hemat{' '}
              {new Intl.NumberFormat('id-ID', { style: 'percent', maximumFractionDigits: 0 }).format(
                calculateSavings({ theMostExpensivePrice, selectedPrice: effectivePrice }),
              )}
            </span>
          )}

          <SelectPrimitive.Icon render={<ChevronDown className='srd:text-blue-100 srd:h-4 srd:w-4' />} />
        </div>
      </SelectPrimitive.Trigger>

      <SelectContent className={containerClassName}>
        <SelectGroup>
          {getAllPrice.map((v, i) => (
            <SelectPrimitive.Item
              className={cn(
                typographyVariants({ variant: 'body-sm' }),
                'srd:relative srd:flex srd:w-full srd:cursor-default srd:select-none srd:items-center srd:rounded-sm srd:py-1.5 srd:pl-8 srd:pr-2 srd:outline-none srd:focus:bg-accent srd:data-disabled:pointer-events-none srd:data-disabled:opacity-50 srd:text-neutral-black',
              )}
              value={v.id}
              key={v.id}
            >
              <span className='srd:absolute srd:left-2 srd:flex srd:h-3.5 srd:w-3.5 srd:items-center srd:justify-center'>
                <SelectPrimitive.ItemIndicator>
                  <Check className='srd:text-blue-100 srd:h-4 srd:w-4' />
                </SelectPrimitive.ItemIndicator>
              </span>

              <div className='srd:flex srd:items-center srd:justify-between srd:w-full'>
                <SelectPrimitive.ItemText className={typographyVariants({ variant: 'body-md' })}>
                  {v.duration} Bulan
                </SelectPrimitive.ItemText>

                <div className='srd:flex srd:items-center srd:gap-x-2'>
                  {showBadges && v.isFavorite && <span className={FAVOURITE_CHIP_CLASS}>Populer</span>}

                  {showBadges && i !== 0 && (
                    <span className={SAVINGS_NOTE_CLASS}>
                      Hemat{' '}
                      {new Intl.NumberFormat('id-ID', { style: 'percent', maximumFractionDigits: 0 }).format(
                        calculateSavings({ theMostExpensivePrice, selectedPrice: v }),
                      )}
                    </span>
                  )}
                </div>
              </div>
            </SelectPrimitive.Item>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

function calculateSavings(props: { theMostExpensivePrice: SelectPrice; selectedPrice: SelectPrice }) {
  const isTheSamePrice = props.selectedPrice.price === props.theMostExpensivePrice.price

  if (isTheSamePrice) return 0

  return (props.theMostExpensivePrice.price - props.selectedPrice.price) / props.theMostExpensivePrice.price
}
